import { useMutation } from "@apollo/client";
import { Box, Checkbox, createStyles, FormControlLabel, Grid, makeStyles, Theme } from "@material-ui/core";
import PhoneField from "components/@phone-field";
import GeoAutoCompletion from "components/GeoAutoCompletion";
import HTTP_STATUS_CODES from "constants/http-status-code";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { ClientRefsAddress } from "generated/graphql";
import { ADD_CLIENT_REF, UPDATE_CLIENT_REF_ADDRESS } from "graphql/Address/mutations";
import { ALL_ADDRESS_BY_USER_ID } from "graphql/Address/queries";
import { useCurrentUser } from "hooks";
import React, { useCallback } from "react";
import { Button } from "ui/Button";
import { TextField } from "ui/TextField";
import { checkEmail, checkIsDefined, fieldEmptyNull, isValidPhoneNumber } from "../utils";

const fields = {
  firstname: "",
  lastname: "",
  email: "",
  phone_number: "",
  address: null as object,
  default: false,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    text_field: {
      marginBottom: "25px",
    },
    formControlLabel: {
      marginTop: "16px",
      marginLeft: "0px",
    },
    checkbox: {
      marginRight: "14px",
    },
    button: {
      marginTop: 10,
    },
    cancelButton: {
      backgroundColor: "#cfecf8",
      color: "#399ebf",
      marginTop: 10,
      },
      root: {
        marginTop: "10px"
    }
  })
);

interface AddAddressFormProps {
  isEdited?: boolean;
  handleCancel: () => void;
  addressToUpdate?: ClientRefsAddress;
}

const AddAddressForm = ({ isEdited, handleCancel, addressToUpdate }: AddAddressFormProps) => {
  const classes = useStyles();
  const { text } = useText();
  const [clientAddress, setClientAddress] = React.useState(fields);
  const [clientRefAddressdata, setClientRefAddressData] = React.useState(null);
  const [isDefaultAddressCheck, setIsDefaultAddressCheck] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(true);
  const { user } = useCurrentUser();
  const dispatchAlert = useAlertDispatch();
  const [addClientRefAddress] = useMutation(ADD_CLIENT_REF);
    const [updateClientRefAddress] = useMutation(UPDATE_CLIENT_REF_ADDRESS);
    
    React.useEffect(() => {
        if (isEdited) {
           setClientRefAddressData(addressToUpdate); 
        }

    },[isEdited])

  const handleClickCloseAdress = () => {
    handleCancel();
  };
  const handleChange = {
    handleAddressChange: useCallback(
        (value: any) => {
        const { description, ...res } = value;
        setClientAddress({ ...clientAddress, address: res });
        setClientRefAddressData({ ...clientRefAddressdata, address: res });
      },
      [clientAddress, clientRefAddressdata]
    ),

    handleNumber: useCallback(
      (value: any) => {
        setClientAddress({ ...clientAddress, phone_number: value });
        setClientRefAddressData({ ...clientRefAddressdata, phone_number: value });
      },
      [clientAddress, clientRefAddressdata]
    ),

    handleInputChange: useCallback(
      (e: any) => {
        setClientAddress({ ...clientAddress, [e.target.name]: e.target.value });
        setClientRefAddressData({ ...clientRefAddressdata, [e.target.name]: e.target.value });
      },
      [clientAddress, clientRefAddressdata]
    ),

    handleCheckboxChange: useCallback(() => {
      setIsDefaultAddressCheck(!isDefaultAddressCheck);
      setClientAddress({ ...clientAddress, default: isDefaultAddressCheck });
      setClientRefAddressData({ ...clientRefAddressdata, default: isDefaultAddressCheck });
    }, [clientAddress, clientRefAddressdata, isDefaultAddressCheck]),
  };

  const handleChekErrors: any = {
    isEmailError: React.useMemo(() => {
      if (!fieldEmptyNull(clientAddress?.email)) {
        return false;
      }

      if (!checkEmail(clientAddress?.email)) {
        return true;
      }
    }, [clientAddress?.email]),

    isPhoneError: React.useMemo(
      () => fieldEmptyNull(clientAddress?.phone_number) && !isValidPhoneNumber(clientAddress?.phone_number || ""),

      [clientAddress?.phone_number]
    ),

    isTextFieldSurnameError: React.useMemo(
      () => !isSubmitted && !fieldEmptyNull(clientAddress?.firstname),
      [clientAddress?.firstname]
    ),

    isTextFieldnameError: React.useMemo(
      () => !isSubmitted && !fieldEmptyNull(clientAddress?.lastname),
      [clientAddress?.lastname]
    ),

    isLocationError: React.useMemo(() => {
      if (checkIsDefined(clientAddress?.address)) {
        setIsSubmitted(false);
      }
    }, [clientAddress?.address]),
  };

  const handleSubmit = async () => {
    setIsSubmitted(true);

    if (isEdited) {
      const clientAddress = {
        firstname: clientRefAddressdata?.firstname,
        lastname: clientRefAddressdata?.lastname,
        phone_number: clientRefAddressdata?.phone_number,
        email: clientRefAddressdata?.email,
        address: {
          address: clientRefAddressdata?.address.address,
          place_id: clientRefAddressdata?.address.place_id,
        },
        default: clientRefAddressdata?.default,
      };

      await updateClientRefAddress({
        variables: {
          input: {
            clientAddress,
            id: clientRefAddressdata._id,
          },
        },
        refetchQueries: [
          {
            query: ALL_ADDRESS_BY_USER_ID,
            variables: {
              params: { clientID: user.company_user_clientId },
            },
          },
        ],
      })
        .then((response) => {
          if (response.data.updateClientRefAddress.code === HTTP_STATUS_CODES.CREATED) {
            dispatchAlert({
              message: response.data.updateClientRefAddress.message,
              severity: "success",
            });
            setClientAddress(fields);
            setIsSubmitted(false);
            setClientRefAddressData(null);
            handleCancel();
          } else {
            dispatchAlert({
              message: response.data.updateClientRefAddress.message,
              severity: "error",
            });
            handleCancel();
          }
        })
        .catch((error) => {
          dispatchAlert({
            message: error.message,
            severity: "error",
          });
        });
    } else {
      const input = {
        clientAddress,
        clientID: user.company_user_clientId,
      };

      await addClientRefAddress({
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: ALL_ADDRESS_BY_USER_ID,
            variables: {
              params: { clientID: user.company_user_clientId },
            },
          },
        ],
      })
        .then((response) => {
          if (response.data.addClientRefAddress.code === HTTP_STATUS_CODES.CREATED) {
            dispatchAlert({
              message: "creation success",
              severity: "success",
            });
            setClientAddress(fields);
            setIsSubmitted(false);
            setClientRefAddressData(null);
            handleCancel();
          } else if (response.data.addClientRefAddress.code === HTTP_STATUS_CODES.BAD_REQUEST) {
            dispatchAlert({
              message: response.data.addClientRefAddress.message,
              severity: "warning",
            });
            handleCancel();
          }
        })
        .catch((error) => {
          dispatchAlert({
            message: error.message,
            severity: "error",
          });
        });
    }
  };

  return (
    <Grid container spacing={1} className={classes.root}>
      <Grid item xs={12}>
        <TextField
          className={classes.text_field}
          fullWidth
          name="firstname"
          label={text.all.address_name}
          variant="outlined"
          required
          value={
            isEdited
              ? clientRefAddressdata?.firstname
              : fieldEmptyNull(clientAddress?.firstname)
              ? clientAddress?.firstname
              : null
          }
          onChange={handleChange.handleInputChange}
          placeholder={text.all.address_name}
          error={handleChekErrors.isTextFieldSurnameError}
          helperText={handleChekErrors.isTextFieldSurnameError ? text.all.this_field_is_required : ""}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          className={classes.text_field}
          fullWidth
          name="lastname"
          label={`${text.all.first_name} ${text.all.last_name} `}
          variant="outlined"
          required
          value={
            isEdited
              ? clientRefAddressdata?.lastname
              : fieldEmptyNull(clientAddress?.lastname)
              ? clientAddress?.lastname
              : null
          }
          onChange={handleChange.handleInputChange}
          placeholder={`${text.all.first_name} ${text.all.last_name} `}
          error={handleChekErrors.isTextFieldnameError}
          helperText={handleChekErrors.isTextFieldnameError ? text.all.this_field_is_required : ""}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          className={classes.text_field}
          fullWidth
          name="email"
          label={text.all.email}
          variant="outlined"
          required
          value={
            isEdited ? clientRefAddressdata?.email : fieldEmptyNull(clientAddress?.email) ? clientAddress?.email : null
          }
          onChange={handleChange.handleInputChange}
          placeholder="exemple@exemple.com"
          error={handleChekErrors.isEmailError}
          helperText={handleChekErrors.isEmailError && text.all.email_invalid}
        />
      </Grid>
      <Grid item xs={12}>
        <PhoneField
          className={classes.text_field}
          fullWidth
          name="phone"
          label={text.all.phone}
          required
          value={
            isEdited
              ? clientRefAddressdata?.phone_number
              : fieldEmptyNull(clientAddress?.phone_number)
              ? clientAddress?.phone_number
              : null
          }
          onValueChange={handleChange.handleNumber}
          placeholder="+221770000000"
          error={handleChekErrors.isPhoneError}
          helperText={handleChekErrors.isPhoneError ? text.all.number_invalid : ""}
        />
      </Grid>
      <Grid item xs={12}>
        <Box style={{ marginTop: "25px" }}>
          <GeoAutoCompletion
            address={
              isEdited
                ? clientRefAddressdata?.address
                : checkIsDefined(clientAddress?.address)
                ? clientAddress?.address
                : null
            }
            onChange={handleChange.handleAddressChange}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          className={classes.formControlLabel}
          label="Set as default address"
          control={
            <Checkbox
              className={classes.checkbox}
              checked={isEdited ? clientRefAddressdata?.default : clientAddress?.default}
              onChange={handleChange.handleCheckboxChange}
            />
          }
        />
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Button className={classes.cancelButton} variant="contained" fullWidth onClick={handleClickCloseAdress}>
            {text.all.cancel}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            className={classes.button}
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={!clientRefAddressdata?.phone_number}
            fullWidth
          >
            {isEdited ? text.all.update : text.all.confirm}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AddAddressForm;
